import { Component, OnInit, Output, Input, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Permissionset } from 'app/shared/models/permissionset';
import {
  Destroy,
  SortExpression,
  tr,
  SearchConfig,
  Types,
  DialogService,
  IQueryResult,
  KeyBase,
  DialogResult,
  Key,
} from '@kpd/framework-ngx';
import { Permission } from 'app/shared/models/permission';
import { PermissionsetService } from 'app/shared/services/permissionset.service';
import { PermissionsetKey } from 'app/shared/models/keys/permissionset-key';
import { SortEvent } from 'primeng/components/common/sortevent';
import { of } from 'rxjs';
import { switchMap, map, tap } from 'rxjs/operators';

import { AuthService } from '@kpd/framework-ngx';
import { ViewMode } from '@kpd/framework-ngx';
import { PermissionsetFacade } from 'app/shared/services/facades/permissionset.facade';
import { Permissions } from 'app/shared/services/permissions';
import { TableController } from 'app/shared/utils/table-controller';

@Component({
  selector: 'app-permission-set-details-permissions',
  templateUrl: './permissionset-details-permissions.component.html',
})
export class PermissionsetDetailsPermissionsComponent extends TableController implements OnInit, OnChanges, OnDestroy {
  Permissions: typeof Permissions = Permissions;
  @Input()
  permissionSet: Permissionset;
  @Input()
  canManage = true;
  @Output()
  switchMode = new EventEmitter();
  @Output()
  permissionCountChanged = new EventEmitter<number>();
  @Destroy()
  destroy$: any;
  readOnlyMode = true;
  selectedKeys: Key<string>[] = [];
  selection = [];
  sortExpression: SortExpression;
  columnsConfig = this.createColumnsConfig();
  saveErrors: string[] = [];
  isKpdSet: boolean;

  linkPermissionsColumnsConfig = this.createColumnsConfig();
  linkPermissionsSearchConfig = this.createLinkPermissionsSearchConfig();

  constructor(
    private permissionSetService: PermissionsetService,
    private permissionSetFacade: PermissionsetFacade,
    private dialogService: DialogService,
    private authService: AuthService,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.authService.getIdentity().hasPermission(Permissions.UserLicenseUpdate)) {
      this.readOnlyMode = false;
    }
    this.isKpdSet = this.getIsKpdSet();
  }

  getIsKpdSet(): boolean {
    if (this.permissionSet.isKpdSet) {
      return this.permissionSet.isKpdSet;
    }
    return false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.permissionSet) {
      this.selectedKeys = [];
      this.selection = [];
      this.saveErrors = [];
      if (
        changes.permissionSet.currentValue &&
        (changes.permissionSet.currentValue as Permissionset).key &&
        (changes.permissionSet.currentValue as Permissionset).key.isDefined()
      ) {
        this.items = this.permissionSet.permissions as Permission[];
      }
    }
  }

  loadPermissionSetPermissions(permissionSetKey: PermissionsetKey): void {
    this.permissionSetFacade.detailLoadingStarted();
    this.permissionSetService.getPermissionSetPermissions(permissionSetKey).subscribe(
      (permissions) => {
        this.items = permissions;
        this.permissionSet.permissions = permissions;
        this.permissionCountChanged.emit(permissions.length);
        this.permissionSetFacade.detailLoadingFinished();
      },
      () => {
        this.permissionSetFacade.detailLoadingFinished();
      },
    );
  }

  onSort(event: SortEvent): void {
    this.sortExpression = new SortExpression();
    this.sortExpression.fieldName = event.field as string;
    this.sortExpression.sortOrder = event.order as number;
  }

  onAdd(): void {
    // load initial data
    this.permissionSetFacade.detailLoadingStarted();
    this.permissionSetService
      .getManageablePermissions(this.permissionSet.application as string)
      .pipe(
        map((userPermissions) => {
          return { result: userPermissions.map((p) => new Permission(p)), totalCount: userPermissions.length };
        }),
        switchMap((queryResult: IQueryResult) => {
          this.permissionSetFacade.detailLoadingFinished();
          const inputData: { queryResult: IQueryResult; keysToExclude: KeyBase[] } = {
            queryResult: queryResult,
            keysToExclude: this.items.map((permission) => permission.key),
          };
          console.log('inputData', inputData);
          return this.dialogService.open('linkEntities', inputData).afterClosed();
        }),
      )
      .subscribe((selectedKeys?: any) => {
        if (!selectedKeys) {
          return;
        }
        this.saveErrors = [];
        this.permissionSetFacade.detailLoadingStarted();
        // re-load resource suppliers after a successful add
        return this.permissionSetService
          .addPermissionToPermissionSet(this.permissionSet.key, selectedKeys)
          .pipe(tap(() => this.loadPermissionSetPermissions(this.permissionSet.key)))
          .subscribe(
            () => {
              this.permissionSetFacade.detailLoadingFinished();
            },
            () => {
              this.saveErrors = [tr('Something went wrong linking the permission(s).')];
              this.permissionSetFacade.detailLoadingFinished();
            },
          );
      });
  }

  doesUserHavePermission(permissionToCheck: Permission, userPermissionsets: Permissionset[]): boolean {
    return userPermissionsets.some((permSet) =>
      (permSet.permissions as Permission[]).some((perm) => perm.key.id === permissionToCheck.key.id),
    );
  }

  onDelete(): void {
    this.dialogService
      .open('confirmPermissionSetPermissionDelete', {
        message: tr('Are you sure you want to unassign the permission(s)?'),
        header: tr('Unassign permission(s)'),
        verifyButtonText: tr('Unassign'),
        verifyButtonStyle: 'danger',
        cancelButtonText: tr('Cancel'),
      })
      .afterClosed()
      .subscribe((result: DialogResult) => {
        this.saveErrors = [];
        if (result === DialogResult.Verify) {
          this.permissionSetFacade.detailLoadingStarted();
          return this.permissionSetService
            .removePermissionFromPermissionSet(this.permissionSet.key as PermissionsetKey, this.selectedKeys)
            .pipe(tap(() => this.loadPermissionSetPermissions(this.permissionSet.key as PermissionsetKey)))
            .subscribe(
              () => {
                this.selectedKeys = [];
                this.permissionSetFacade.detailLoadingFinished();
              },
              () => {
                this.permissionSetFacade.detailLoadingFinished();
                this.saveErrors = [tr('Something went wrong unlinking the permission(s).')];
              },
            );
        }
      });
  }

  toggleReadOnly(): void {
    this.readOnlyMode = !this.readOnlyMode;
    this.switchMode.emit(this.readOnlyMode ? ViewMode.Consult : ViewMode.Edit);
  }

  isPermissionSetAvailable(): boolean {
    return this.permissionSet instanceof Permissionset;
  }

  createColumnsConfig(): any[] {
    return [
      { field: 'name', header: tr('Name'), width: '30%', type: 'nameDeprecated' },
      { field: 'description', header: tr('Description'), width: '70%' },
    ];
  }

  createLinkPermissionsSearchConfig(): SearchConfig {
    return new SearchConfig({
      multipleCriteria: true,
      fields: [
        {
          field: 'name',
          displayValue: tr('Name'),
          dataType: Types.String,
        },
        {
          field: 'description',
          displayValue: tr('Description'),
          dataType: Types.String,
        },
      ],
    });
  }

  onImport(): void {
    this.dialogService
      .open('importPermissions')
      .afterClosed()
      .pipe(
        switchMap((result) => {
          this.permissionSetFacade.detailLoadingStarted();
          if (result) {
            return this.permissionSetService.getById(this.permissionSet.key);
          }
          return of(this.permissionSet);
        }),
      )
      .subscribe(
        () => {
          this.permissionSetFacade.detailLoadingFinished();
          this.loadPermissionSetPermissions(this.permissionSet.key);
        },
        () => {
          this.permissionSetFacade.detailLoadingFinished();
        },
      );
  }

  // tslint:disable-next-line:no-unused-expression
  ngOnDestroy(): void {}
}
